import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaFileContract, FaArrowLeft } from 'react-icons/fa';
import PageTransition from '../components/PageTransition';
import { fadeInUp, staggerContainer } from '../utils/animations';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-black text-white mb-4 flex items-center gap-2">
      <span className="w-1 h-5 bg-white rounded-full inline-block" />
      {title}
    </h2>
    <div className="text-gray-400 leading-relaxed space-y-3 text-sm">{children}</div>
  </div>
);

const Terms = () => (
  <PageTransition>
    <div className="bg-transparent min-h-screen">
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-3xl">

          <motion.div variants={fadeInUp} initial="hidden" animate="visible" className="mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm font-semibold transition-colors duration-200 group">
              <FaArrowLeft className="text-xs group-hover:-translate-x-0.5 transition-transform duration-200" />
              Back to Home
            </Link>
          </motion.div>

          <motion.div variants={staggerContainer} initial="hidden" animate="visible">
            <motion.div variants={fadeInUp} className="inline-flex mb-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/60 border border-gray-700 rounded-full">
                <FaFileContract className="text-gray-400 text-xs" />
                <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">Legal</span>
              </div>
            </motion.div>

            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-white mb-4">
              Terms of Service
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-gray-500 text-sm mb-12">
              Last updated: March 2026
            </motion.p>

            <motion.div variants={fadeInUp} className="glass-card rounded-2xl p-8 md:p-10 space-y-2">
              <Section title="Agreement to Terms">
                <p>By accessing or using the Zynexis website and services, you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, you may not access our services.</p>
                <p>These Terms of Service apply to all visitors, users, and others who access or use our website and services.</p>
              </Section>

              <Section title="Services">
                <p>Zynexis provides software engineering, AI development, product design, and technology consulting services. The specific scope of work, deliverables, timelines, and fees for any engagement will be set out in a separate Statement of Work (SOW) or Service Agreement signed by both parties.</p>
                <p>We reserve the right to modify, suspend, or discontinue any part of our services at any time with reasonable notice.</p>
              </Section>

              <Section title="Intellectual Property">
                <p>Upon full payment of all fees, all custom work product created specifically for a client ("Deliverables") shall be owned by the client, unless otherwise agreed in the SOW.</p>
                <p>Zynexis retains ownership of:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li>All pre-existing intellectual property, tools, frameworks, and methodologies</li>
                  <li>General knowledge, skills, and experience gained during an engagement</li>
                  <li>Any open-source components used in Deliverables (subject to their respective licences)</li>
                </ul>
                <p className="mt-2">Our website content, branding, and materials are protected by copyright and may not be reproduced without our written permission.</p>
              </Section>

              <Section title="Client Responsibilities">
                <p>Clients agree to:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li>Provide timely feedback, approvals, and access to necessary resources</li>
                  <li>Ensure all information provided to us is accurate and complete</li>
                  <li>Pay invoices in accordance with agreed payment terms</li>
                  <li>Not use our services for any unlawful purpose</li>
                  <li>Maintain the confidentiality of any access credentials provided</li>
                </ul>
              </Section>

              <Section title="Payment Terms">
                <p>Payment terms are as specified in your SOW or invoice. Unless otherwise agreed:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li>Invoices are due within 14 days of issue</li>
                  <li>Late payments may incur interest at 1.5% per month</li>
                  <li>Work may be paused on accounts overdue by more than 30 days</li>
                  <li>Deposits are non-refundable unless we fail to commence work</li>
                </ul>
              </Section>

              <Section title="Confidentiality">
                <p>Both parties agree to keep confidential any proprietary or sensitive business information shared during the course of an engagement. This obligation does not apply to information that is publicly available, independently developed, or required to be disclosed by law.</p>
              </Section>

              <Section title="Limitation of Liability">
                <p>To the maximum extent permitted by law, Zynexis shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill arising from your use of our services.</p>
                <p className="mt-2">Our total liability for any claim arising under these Terms shall not exceed the total fees paid by you in the three months preceding the claim.</p>
              </Section>

              <Section title="Warranties & Disclaimers">
                <p>We warrant that our services will be performed with reasonable skill and care. Beyond this, our services are provided "as is" without warranties of any kind, express or implied.</p>
                <p className="mt-2">We do not warrant that our website will be uninterrupted, error-free, or free from harmful components.</p>
              </Section>

              <Section title="Termination">
                <p>Either party may terminate a service engagement with 30 days' written notice. Upon termination, the client shall pay for all work completed up to the termination date. We may terminate immediately if you breach these Terms or engage in conduct harmful to our team or reputation.</p>
              </Section>

              <Section title="Governing Law">
                <p>These Terms shall be governed by and construed in accordance with applicable law. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the relevant courts.</p>
              </Section>

              <Section title="Changes to Terms">
                <p>We reserve the right to update these Terms at any time. Material changes will be communicated via email or prominent notice on our website. Continued use of our services after changes constitutes acceptance of the new Terms.</p>
              </Section>

              <Section title="Contact Us">
                <p>For questions about these Terms of Service, please contact us:</p>
                <div className="mt-3 p-4 bg-gray-900/60 rounded-xl border border-gray-700 space-y-1">
                  <p><strong className="text-gray-300">Zynexis</strong></p>
                  <p>Email: <a href="mailto:contact@zynexis.com" className="text-white hover:underline">contact@zynexis.com</a></p>
                </div>
              </Section>
            </motion.div>
          </motion.div>

        </div>
      </section>
    </div>
  </PageTransition>
);

export default Terms;
